import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  View, Text, StyleSheet, TouchableOpacity, ScrollView, SafeAreaView, StatusBar,
  Image, Linking, Animated, ActivityIndicator, Share, Alert, RefreshControl, Dimensions,
} from 'react-native';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { apiFetch } from '../../utils/apiClient';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';
import useProfile from '../../hooks/useProfile';

let LinearGradient;
try { LinearGradient = require('expo-linear-gradient').LinearGradient; }
catch { LinearGradient = ({colors,style,children,...p})=><View style={[style,{backgroundColor:colors?.[0]||'#0e4e96'}]} {...p}>{children}</View>; }

const API_BASE = 'https://api.jobeng.ai';
const API_KEY  = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const PROF_KEYS = { PROFILE:'prof_user_profile' };
const SAVED_KEY = 'grad_saved_programs';
const APPS_KEY  = 'grad_applications';

const T = {primary:'#0e4e96',primaryLight:'#1e7be2',primaryBg:'#EFF6FF',success:'#10b981',successBg:'#D1FAE5',warning:'#f59e0b',warningBg:'#FEF3C7',danger:'#ef4444',dangerBg:'#FEE2E2',text:'#1F2937',textSec:'#64748B',textMut:'#94A3B8',border:'#E2E8F0',borderLight:'#F1F5F9',card:'#FFF',bg:'#F8FAFC',white:'#FFF'};

const daysLeft = d => { try { return Math.ceil((new Date(d)-Date.now())/(864e5)); } catch { return null; } };
const fmtDate = d => { try { return new Date(d).toLocaleDateString('en-GB',{day:'numeric',month:'long',year:'numeric'}); } catch { return ''; } };
const parseList = v => { if(!v)return[]; if(Array.isArray(v))return v; if(typeof v==='string'){try{const p=JSON.parse(v);if(Array.isArray(p))return p;}catch{} return v.split('\n').map(s=>s.replace(/^[•\-*]\s*/,'').trim()).filter(Boolean);} return []; };

const TABS = [
  {key:'overview',label:'Overview',icon:'document-text-outline'},
  {key:'requirements',label:'Requirements',icon:'checkmark-circle-outline'},
  {key:'benefits',label:'Benefits',icon:'gift-outline'},
  {key:'company',label:'Company',icon:'business-outline'},
  {key:'similar',label:'Similar',icon:'copy-outline'},
];

export default function GraduateProgramDetailsScreen({ navigation, route }) {
  // ── Profile integration (auto-injected) ──
  const profileCtx = useProfile();
  const programId = route.params?.programId || route.params?.id;
  const initialProgram = route.params?.program || route.params?.programData || null;

  const [isLoading, setIsLoading] = useState(true);
  const [prog, setProg]           = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [isSaved, setIsSaved]     = useState(false);
  const [hasApplied, setHasApplied] = useState(false);
  const [matchScore, setMatchScore] = useState(0);
  const [userSkills, setUserSkills] = useState([]);
  const [similar, setSimilar]     = useState([]);
  const [error, setError]         = useState(null);

  const fadeAnim  = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(30)).current;

  useEffect(() => { loadDetails(); }, []);

  const loadDetails = async () => {
    setIsLoading(true); setError(null);
    try {
      let data = initialProgram;
      if (!data && programId) {
        const r = await apiFetch(`/api/data/internship_programs/${programId}`);
        if (!r.error) data = r.data || r;
      }
      if (!data) { setError('Program not found.'); setIsLoading(false); return; }

      data = { ...data, requirements:parseList(data.requirements), responsibilities:parseList(data.responsibilities), benefits:parseList(data.benefits), skills:parseList(data.skills), perks:parseList(data.perks) };
      setProg(data);

      // Profile
      const profRaw = await AsyncStorage.getItem(PROF_KEYS.PROFILE)||await AsyncStorage.getItem('userProfile');
      if (profRaw) {
        const prof = JSON.parse(profRaw);
        const sk = (prof.skills||[]).map(s=>(s.name||s.skill_name||'').toLowerCase()).filter(Boolean);
        setUserSkills(sk);
        const tags = [...(data.requirements||[]),...(data.skills||[])].map(s=>(typeof s==='string'?s:'').toLowerCase());
        if (tags.length) { const m=tags.filter(t=>sk.some(u=>t.includes(u)||u.includes(t))).length; setMatchScore(Math.min(100,Math.round((m/tags.length)*80)+15)); }
      }

      // Saved
      try { const raw=await AsyncStorage.getItem(SAVED_KEY); if(raw) setIsSaved(JSON.parse(raw).includes(data.id||programId)); } catch{}
      // Applied
      try { const raw=await AsyncStorage.getItem(APPS_KEY); if(raw) setHasApplied(JSON.parse(raw).some(a=>a.programId===(data.id||programId))); } catch{}
      // Similar
      const sr = await apiFetch('/api/data/internship_programs?is_active=true&limit=5');
      if (!sr.error && Array.isArray(sr)) setSimilar(sr.filter(p=>p.id!==(data.id||programId)).slice(0,4));

      Animated.parallel([
        Animated.timing(fadeAnim,{toValue:1,duration:600,useNativeDriver:true}),
        Animated.timing(slideAnim,{toValue:0,duration:600,useNativeDriver:true}),
      ]).start();
    } catch { setError('Failed to load.'); }
    setIsLoading(false);
  };

  const toggleSave = async () => {
    try {
      const raw = await AsyncStorage.getItem(SAVED_KEY);
      let saved = raw ? JSON.parse(raw) : [];
      const id = prog?.id || programId;
      if (isSaved) saved = saved.filter(s=>s!==id); else saved.push(id);
      await AsyncStorage.setItem(SAVED_KEY, JSON.stringify(saved));
      setIsSaved(!isSaved);
    } catch {}
  };

  const handleShare = async () => {
    if (!prog) return;
    try { await Share.share({title:prog.title,message:`${prog.title} at ${prog.company}\n${prog.location} · ${prog.duration}\n${prog.compensation||''}\n\nApply on JOBENG!`}); } catch{}
  };

  const handleApply = () => {
    if (hasApplied) { Alert.alert('Already Applied','You have already applied to this program.'); return; }
    navigation.navigate('GraduateProgramApplication',{programId:prog?.id||programId,program:prog});
  };

  const deadline = useMemo(() => daysLeft(prog?.deadline||prog?.application_deadline), [prog]);
  const closed = deadline!==null && deadline<0;

  const ListItem = ({text,icon='checkmark-circle',color=T.success}) => (
    <View style={{flexDirection:'row',alignItems:'flex-start',marginBottom:10,gap:10}}>
      <Ionicons name={icon} size={16} color={color} style={{marginTop:2}}/>
      <Text style={{fontSize:14,color:'#475569',lineHeight:20,flex:1}}>{typeof text==='string'?text:String(text)}</Text>
    </View>
  );

  if (isLoading) return <SafeAreaView style={{flex:1,backgroundColor:T.bg}}><StatusBar barStyle="light-content"/><View style={{flex:1,justifyContent:'center',alignItems:'center'}}><ActivityIndicator size={36} color={T.primary}/></View></SafeAreaView>;
  if (error||!prog) return <SafeAreaView style={{flex:1,backgroundColor:T.bg}}><StatusBar barStyle="light-content"/><View style={{flex:1,justifyContent:'center',alignItems:'center',padding:32}}><Ionicons name="alert-circle" size={48} color={T.danger}/><Text style={{fontSize:18,fontWeight:'700',color:T.text,marginTop:12}}>{error||'Not Found'}</Text><TouchableOpacity style={{flexDirection:'row',alignItems:'center',backgroundColor:T.primary,paddingHorizontal:20,paddingVertical:10,borderRadius:12,marginTop:16}} onPress={()=>navigation.goBack()}><Ionicons name="arrow-back" size={18} color={T.white}/><Text style={{color:T.white,fontWeight:'600',marginLeft:6}}>Go Back</Text></TouchableOpacity></View></SafeAreaView>;

  return (
    <SafeAreaView style={{flex:1,backgroundColor:T.primary}}>
      <StatusBar barStyle="light-content" backgroundColor={T.primary}/>
      {/* Header */}
      <LinearGradient colors={[T.primary,T.primaryLight]} style={st.header}>
        <TouchableOpacity style={st.hdrBtn} onPress={()=>navigation.goBack()}><Ionicons name="arrow-back" size={22} color={T.white}/></TouchableOpacity>
        <View style={{flex:1,marginHorizontal:12}}><Text style={st.hdrTitle} numberOfLines={1}>Program Details</Text><Text style={st.hdrSub} numberOfLines={1}>{prog.company}</Text></View>
        <TouchableOpacity style={st.hdrBtn} onPress={handleShare}><Ionicons name="share-outline" size={20} color={T.white}/></TouchableOpacity>
        <TouchableOpacity style={[st.hdrBtn,{marginLeft:6}]} onPress={toggleSave}><Ionicons name={isSaved?'bookmark':'bookmark-outline'} size={20} color={T.white}/></TouchableOpacity>
      </LinearGradient>

      <ScrollView style={{flex:1,backgroundColor:T.bg}} showsVerticalScrollIndicator={false} refreshControl={<RefreshControl refreshing={false} onRefresh={loadDetails}/>}>
        {/* Hero */}
        <Animated.View style={[st.hero,{opacity:fadeAnim,transform:[{translateY:slideAnim}]}]}>
          <View style={{flexDirection:'row',marginBottom:14}}>
            <View style={st.logo}><Text style={st.logoT}>{(prog.company||'?')[0]}</Text></View>
            <View style={{flex:1,marginLeft:12}}>
              <Text style={st.jobTitle}>{prog.title}</Text>
              <Text style={st.companyName}>{prog.company}</Text>
              <View style={{flexDirection:'row',flexWrap:'wrap',gap:6,marginTop:6}}>
                <View style={st.chip}><Ionicons name="location-outline" size={12} color={T.textSec}/><Text style={st.chipT}>{prog.location}</Text></View>
                <View style={st.chip}><Ionicons name="time-outline" size={12} color={T.textSec}/><Text style={st.chipT}>{prog.duration}</Text></View>
                <View style={st.chip}><Ionicons name="cash-outline" size={12} color={T.success}/><Text style={[st.chipT,{color:T.success,fontWeight:'700'}]}>{prog.compensation||prog.stipend||'Competitive'}</Text></View>
              </View>
            </View>
          </View>

          {/* Stats */}
          <View style={st.statsRow}>
            <View style={st.statBox}><Text style={[st.statVal,{color:T.primary}]}>{matchScore}%</Text><Text style={st.statLbl}>Match</Text></View>
            <View style={st.statDiv}/>
            <View style={st.statBox}><Text style={[st.statVal,{color:closed?T.danger:deadline<=14?T.warning:T.text}]}>{closed?'Closed':deadline!==null?`${deadline}d`:'TBC'}</Text><Text style={st.statLbl}>Deadline</Text></View>
            <View style={st.statDiv}/>
            <View style={st.statBox}><Text style={[st.statVal,{color:'#8B5CF6'}]}>{prog.rotations||'—'}</Text><Text style={st.statLbl}>Rotations</Text></View>
            <View style={st.statDiv}/>
            <View style={st.statBox}><Text style={[st.statVal,{color:T.primary}]}>{prog.applicants||'—'}</Text><Text style={st.statLbl}>Applicants</Text></View>
          </View>

          {/* Badges */}
          <View style={{flexDirection:'row',flexWrap:'wrap',gap:6,marginTop:10}}>
            {prog.isInternational&&<View style={[st.badge,{backgroundColor:'#DBEAFE'}]}><Text style={[st.badgeT,{color:'#2563EB'}]}>🌍 International</Text></View>}
            {prog.mentorship&&<View style={[st.badge,{backgroundColor:T.successBg}]}><Ionicons name="people" size={10} color={T.success}/><Text style={[st.badgeT,{color:T.success}]}>Mentorship</Text></View>}
            {prog.professionalQualification&&<View style={[st.badge,{backgroundColor:T.warningBg}]}><Ionicons name="ribbon" size={10} color={T.warning}/><Text style={[st.badgeT,{color:T.warning}]}>Prof. Qualification</Text></View>}
            {hasApplied&&<View style={[st.badge,{backgroundColor:T.successBg}]}><Ionicons name="checkmark-circle" size={10} color={T.success}/><Text style={[st.badgeT,{color:T.success}]}>Applied</Text></View>}
            <View style={[st.badge,{backgroundColor:T.primaryBg}]}><Text style={[st.badgeT,{color:T.primary}]}>{prog.field||prog.category||'General'}</Text></View>
          </View>
        </Animated.View>

        {/* Tabs */}
        <View style={st.tabBar}>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{paddingHorizontal:16,gap:6}}>
            {TABS.map(t=>(
              <TouchableOpacity key={t.key} style={[st.tab,activeTab===t.key&&st.tabActive]} onPress={()=>setActiveTab(t.key)}>
                <Ionicons name={t.icon} size={14} color={activeTab===t.key?T.primary:T.textMut}/>
                <Text style={[st.tabT,activeTab===t.key&&st.tabTActive]}>{t.label}</Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>

        {/* Content */}
        <Animated.View style={{opacity:fadeAnim}}>
          {activeTab==='overview'&&(
            <View style={st.section}>
              <Text style={st.secTitle}>Program Description</Text>
              <Text style={st.descText}>{prog.description||'No description available.'}</Text>
              {prog.responsibilities?.length>0&&(<><Text style={st.secTitle}>What You'll Do</Text>{prog.responsibilities.map((r,i)=><ListItem key={i} text={r}/>)}</>)}
              {(prog.workingHours||prog.teamSize)&&(<><Text style={st.secTitle}>Additional Details</Text>
                {prog.workingHours&&<View style={st.detailRow}><Ionicons name="time-outline" size={16} color={T.primary}/><View><Text style={st.detailL}>Working Hours</Text><Text style={st.detailV}>{prog.workingHours}</Text></View></View>}
                {prog.teamSize&&<View style={st.detailRow}><Ionicons name="people-outline" size={16} color={T.primary}/><View><Text style={st.detailL}>Cohort Size</Text><Text style={st.detailV}>{prog.teamSize}</Text></View></View>}
              </>)}
            </View>
          )}
          {activeTab==='requirements'&&(
            <View style={st.section}>
              <Text style={st.secTitle}>Requirements</Text>
              {prog.requirements?.length>0 ? prog.requirements.map((r,i)=>{
                const isMatch = userSkills.some(us=>(typeof r==='string'?r:'').toLowerCase().includes(us));
                return <ListItem key={i} text={r} icon={isMatch?'checkmark-circle':'ellipse-outline'} color={isMatch?T.success:T.textMut}/>;
              }) : <Text style={st.emptyT}>No requirements listed.</Text>}
              {prog.skills?.length>0&&(<><Text style={[st.secTitle,{marginTop:16}]}>Skills</Text><View style={{flexDirection:'row',flexWrap:'wrap',gap:6}}>{prog.skills.map((sk,i)=>{
                const isMatch = userSkills.some(us=>(typeof sk==='string'?sk:'').toLowerCase().includes(us));
                return <View key={i} style={[st.skillTag,isMatch&&{backgroundColor:T.successBg,borderColor:T.success}]}>{isMatch&&<Ionicons name="checkmark" size={10} color={T.success}/>}<Text style={[st.skillTagT,isMatch&&{color:T.success}]}>{sk}</Text></View>;
              })}</View></>)}
            </View>
          )}
          {activeTab==='benefits'&&(
            <View style={st.section}>
              <Text style={st.secTitle}>Benefits & Perks</Text>
              {prog.benefits?.length>0 ? prog.benefits.map((b,i)=><ListItem key={i} text={b} icon="gift" color={T.warning}/>) : <Text style={st.emptyT}>Benefits not listed.</Text>}
              {prog.perks?.length>0&&(<><Text style={[st.secTitle,{marginTop:16}]}>Additional Perks</Text>{prog.perks.map((p,i)=><ListItem key={i} text={p} icon="star" color="#8B5CF6"/>)}</>)}
            </View>
          )}
          {activeTab==='company'&&(
            <View style={st.section}>
              <Text style={st.secTitle}>About {prog.company}</Text>
              <Text style={st.descText}>{prog.companyInfo?.description||`${prog.company} offers an excellent graduate program with real project experience.`}</Text>
              <View style={st.companyStats}>
                {[{i:'business',l:'Industry',v:prog.companyInfo?.industry||prog.industry||prog.field||'—'},{i:'people',l:'Size',v:prog.companyInfo?.size||prog.company_size||'—'},{i:'location',l:'Location',v:prog.location}].map((c,i)=>(
                  <View key={i} style={st.cStatCard}><Ionicons name={c.i} size={18} color={T.primary}/><Text style={st.cStatL}>{c.l}</Text><Text style={st.cStatV}>{c.v}</Text></View>
                ))}
              </View>
              {prog.companyInfo?.website&&<TouchableOpacity style={st.webBtn} onPress={()=>Linking.openURL(prog.companyInfo.website).catch(()=>{})}><Ionicons name="globe-outline" size={18} color={T.primary}/><Text style={{fontSize:14,fontWeight:'600',color:T.primary,marginHorizontal:8}}>Visit Website</Text><Ionicons name="open-outline" size={14} color={T.primary}/></TouchableOpacity>}
              {prog.applicationLink&&<TouchableOpacity style={[st.webBtn,{marginTop:8}]} onPress={()=>Linking.openURL(prog.applicationLink).catch(()=>{})}><Ionicons name="link-outline" size={18} color={T.primary}/><Text style={{fontSize:14,fontWeight:'600',color:T.primary,marginHorizontal:8}}>Application Portal</Text><Ionicons name="open-outline" size={14} color={T.primary}/></TouchableOpacity>}
            </View>
          )}
          {activeTab==='similar'&&(
            <View style={st.section}>
              <Text style={st.secTitle}>Similar Programs</Text>
              {similar.length>0 ? similar.map((sj,i)=>(
                <TouchableOpacity key={sj.id||i} style={st.simCard} onPress={()=>navigation.push('GraduateProgramDetails',{programId:sj.id,program:sj})}>
                  <View style={{flex:1}}>
                    <Text style={{fontSize:14,fontWeight:'700',color:T.text}} numberOfLines={1}>{sj.title}</Text>
                    <Text style={{fontSize:12,color:T.primary,fontWeight:'600',marginTop:2}}>{sj.company}</Text>
                    <Text style={{fontSize:11,color:T.textSec,marginTop:2}}>{sj.location} · {sj.duration}</Text>
                  </View>
                  <Ionicons name="chevron-forward" size={20} color={T.textMut}/>
                </TouchableOpacity>
              )) : <Text style={st.emptyT}>No similar programs found.</Text>}
            </View>
          )}
        </Animated.View>
        <View style={{height:20}}/>
      </ScrollView>

      {/* Action Bar */}
      <View style={st.actionBar}>
        <TouchableOpacity style={[st.applyBtn,hasApplied&&{backgroundColor:T.success},closed&&{backgroundColor:T.textMut}]}
          onPress={handleApply} disabled={hasApplied||closed}>
          <Ionicons name={hasApplied?'checkmark-circle':closed?'lock-closed':'paper-plane'} size={18} color={T.white}/>
          <Text style={st.applyBtnT}>{hasApplied?'Applied':closed?'Closed':'Apply Now'}</Text>
        </TouchableOpacity>
        <TouchableOpacity style={[st.saveBtn2,isSaved&&{backgroundColor:T.primary,borderColor:T.primary}]} onPress={toggleSave}>
          <Ionicons name={isSaved?'bookmark':'bookmark-outline'} size={20} color={isSaved?T.white:T.primary}/>
        </TouchableOpacity>
      </View>
    </SafeAreaView>
  );
}

const st = StyleSheet.create({
  header:{flexDirection:'row',alignItems:'center',paddingHorizontal:16,paddingVertical:14},
  hdrBtn:{width:38,height:38,borderRadius:12,backgroundColor:'rgba(255,255,255,0.18)',justifyContent:'center',alignItems:'center'},
  hdrTitle:{fontSize:18,fontWeight:'700',color:'#FFF'},
  hdrSub:{fontSize:12,color:'rgba(255,255,255,0.75)',marginTop:1},
  hero:{backgroundColor:T.card,padding:18,borderBottomWidth:1,borderBottomColor:T.border},
  logo:{width:56,height:56,borderRadius:16,backgroundColor:T.primaryBg,justifyContent:'center',alignItems:'center'},
  logoT:{fontSize:24,fontWeight:'800',color:T.primary},
  jobTitle:{fontSize:20,fontWeight:'800',color:T.text,lineHeight:26},
  companyName:{fontSize:15,fontWeight:'600',color:T.primary,marginTop:2},
  chip:{flexDirection:'row',alignItems:'center',gap:3,backgroundColor:T.borderLight,paddingHorizontal:8,paddingVertical:4,borderRadius:8},
  chipT:{fontSize:11,fontWeight:'500',color:T.textSec},
  statsRow:{flexDirection:'row',backgroundColor:T.borderLight,borderRadius:14,padding:14},
  statBox:{flex:1,alignItems:'center'},statVal:{fontSize:14,fontWeight:'800'},statLbl:{fontSize:10,color:T.textMut,marginTop:2},statDiv:{width:1,backgroundColor:T.border,marginVertical:4},
  badge:{flexDirection:'row',alignItems:'center',gap:3,paddingHorizontal:8,paddingVertical:4,borderRadius:8},
  badgeT:{fontSize:10,fontWeight:'700'},
  tabBar:{backgroundColor:T.card,borderBottomWidth:1,borderBottomColor:T.border,paddingVertical:8},
  tab:{flexDirection:'row',alignItems:'center',paddingHorizontal:14,paddingVertical:10,borderRadius:20,gap:4},
  tabActive:{backgroundColor:T.primaryBg,borderWidth:1,borderColor:'#BFDBFE'},
  tabT:{fontSize:13,fontWeight:'500',color:T.textMut},tabTActive:{color:T.primary,fontWeight:'700'},
  section:{padding:20,backgroundColor:T.card,marginBottom:1},
  secTitle:{fontSize:17,fontWeight:'700',color:T.text,marginBottom:12},
  descText:{fontSize:14,color:'#475569',lineHeight:22,marginBottom:16},
  emptyT:{fontSize:14,color:T.textMut,fontStyle:'italic',textAlign:'center',paddingVertical:20},
  detailRow:{flexDirection:'row',alignItems:'center',gap:10,backgroundColor:T.borderLight,borderRadius:10,padding:12,marginBottom:8},
  detailL:{fontSize:11,color:T.textMut,fontWeight:'500'},detailV:{fontSize:13,fontWeight:'600',color:T.text,marginTop:1},
  skillTag:{flexDirection:'row',alignItems:'center',gap:3,paddingHorizontal:10,paddingVertical:5,borderRadius:8,borderWidth:1,borderColor:T.border,backgroundColor:T.borderLight},
  skillTagT:{fontSize:11,fontWeight:'600',color:T.textSec},
  companyStats:{flexDirection:'row',gap:8,marginVertical:14},
  cStatCard:{flex:1,alignItems:'center',backgroundColor:T.borderLight,borderRadius:14,padding:14},
  cStatL:{fontSize:11,color:T.textMut,marginTop:6,fontWeight:'500'},cStatV:{fontSize:12,fontWeight:'700',color:T.text,marginTop:2,textAlign:'center'},
  webBtn:{flexDirection:'row',alignItems:'center',justifyContent:'center',backgroundColor:T.primaryBg,borderWidth:1,borderColor:'#BFDBFE',borderRadius:12,paddingVertical:12},
  simCard:{flexDirection:'row',alignItems:'center',backgroundColor:T.borderLight,borderRadius:14,padding:14,marginBottom:10,borderWidth:1,borderColor:T.border},
  actionBar:{flexDirection:'row',paddingHorizontal:16,paddingVertical:12,backgroundColor:T.card,borderTopWidth:1,borderTopColor:T.border,gap:10},
  applyBtn:{flex:1,flexDirection:'row',alignItems:'center',justifyContent:'center',backgroundColor:T.primary,borderRadius:14,paddingVertical:14,gap:8},
  applyBtnT:{fontSize:15,fontWeight:'700',color:T.white},
  saveBtn2:{width:52,height:52,borderRadius:14,justifyContent:'center',alignItems:'center',borderWidth:2,borderColor:T.primary,backgroundColor:T.card},
});
